import useProjectDetails from '@/hooks/projects/useProjectDetails';
import useProjects from '@/hooks/projects/useProjects';
import type { Project } from '@/interfaces/projects.interface';
import { useLocalStorage } from 'primereact/hooks';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export const PROJECT_ID_KEY = 'selectedProjectId';

export type ProjectDetailsStatus = 'idle' | 'loading' | 'loaded' | 'failed';

interface ProjectContextType {
  projects: Project[];
  dropdownListCache: Project[];
  dropdownHasMore: boolean;
  replaceDropdownListCache: (projects: Project[], hasMore: boolean) => void;
  cachedSelectedProject: Project | null;
  setCachedSelectedProject: (project: Project | null) => void;
  fetchProjects: () => void;
  /** Increments when project lists should refetch (e.g. after create/delete). */
  projectsRefreshKey: number;
  notifyProjectsChanged: () => Promise<void>;
  /**
   * Projects this session has successfully deleted.
   *
   * The delete returns 204 and the very next `GET /projects` still lists the
   * project — measured: DELETE and both refetches inside the same second, the
   * project present in each response. So a list is not stale, it is being told
   * the project still exists. Nothing in the payload marks it as going away
   * (`projectId, projectName, creationDate, updatedDate` and nothing else), so
   * this is the only place that knows, and lists filter against it.
   *
   * A client-side mitigation for a server-side read-after-delete lag, not a
   * fix for it. It lasts the session; a reload clears it, by which time the
   * backend has caught up.
   */
  deletedProjectIds: string[];
  markProjectDeleted: (projectId: string) => void;
  loadingProjects: boolean;
  selectedProjectId: string | null;
  setSelectedProjectName: (projectName: string | null) => void;
  setSelectedProjectId: (projectId: string | null) => void;
  projectNeo4jDatabase: string | null;
  /**
   * Where reading the selected project's details stands. Until they are
   * `loaded`, a null `projectNeo4jDatabase` means unknown — not a project that
   * has no graph database.
   */
  projectDetailsStatus: ProjectDetailsStatus;
  reloadProjectDetails: () => void;
  selectedProjectName: string | null;
  isProjectSelectorVisible: boolean;
  setProjectSelectorVisible: (visible: boolean) => void;
  selectedAdminPanelProjectId: string | null;
  selectedAdminPanelProjectDBName: string | null;
  setSelectedAdminPanelProject: (projectId: string | null, dbName: string | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider = ({ 
  children, 
  initialProjectId 
}: { 
  children: ReactNode;
  initialProjectId?: string | null;
}) => {
  const { projects, fetchProjects, loading: loadingProjects } = useProjects();
  const [projectsRefreshKey, setProjectsRefreshKey] = useState(0);
  const [dropdownListCache, setDropdownListCache] = useState<Project[]>([]);
  const [dropdownHasMore, setDropdownHasMore] = useState(true);
  const [cachedSelectedProject, setCachedSelectedProject] =
    useState<Project | null>(null);
  const [deletedProjectIds, setDeletedProjectIds] = useState<string[]>([]);

  const markProjectDeleted = useCallback((projectId: string) => {
    setDeletedProjectIds((previous) =>
      previous.includes(projectId) ? previous : [...previous, projectId],
    );
  }, []);

  const replaceDropdownListCache = useCallback((list: Project[], hasMore: boolean) => {
    setDropdownListCache(list.length ? [...list] : []);
    setDropdownHasMore(hasMore);
  }, []);

  const notifyProjectsChanged = useCallback(async () => {
    // Bump refreshKey for paginated list views and refresh the shared global cache.
    setProjectsRefreshKey((key) => key + 1);
    // The dropdown's cached page has to go with them. It is seeded into a
    // mounting `ProjectDropdown`, which then skips its own prefetch entirely
    // whenever the cache is non-empty — so after a project was deleted and
    // recreated under the same name, the dropdown kept serving the DELETED
    // project, complete with its old id, until a browser reload emptied this
    // in-memory cache. That reload was the only known workaround (PD-109).
    setDropdownListCache([]);
    setDropdownHasMore(true);
    await fetchProjects();
  }, [fetchProjects]);

  const { fetchProjectById } = useProjectDetails();
  const [selectedProjectId, setSelectedProjectId] = useLocalStorage<
    string | null
  >(initialProjectId || null, PROJECT_ID_KEY);
  const [projectNeo4jDatabase, setProjectNeo4jDatabase] = useState<
    string | null
  >(null);
  const [selectedProjectName, setSelectedProjectName] = useState<string | null>(
    null
  );
  const [projectDetailsStatus, setProjectDetailsStatus] =
    useState<ProjectDetailsStatus>('idle');
  const [isProjectSelectorVisible, setProjectSelectorVisible] = useState(false);
  const [selectedAdminPanelProjectId, setSelectedAdminPanelProjectId] = useState<string | null>(null);
  const [selectedAdminPanelProjectDBName, setSelectedAdminPanelProjectDBName] = useState<string | null>(null);

  const setSelectedAdminPanelProject = (projectId: string | null, dbName: string | null) => {
    setSelectedAdminPanelProjectId(projectId);
    setSelectedAdminPanelProjectDBName(dbName);
  };

  // Generation counter so a slow earlier `fetchProjectById` cannot overwrite the
  // result of a newer project switch (race when the user changes projects fast).
  const projectDetailsGenRef = useRef(0);

  const setProjectDetails = async (projectId: string, requestId: number) => {
    const result = await fetchProjectById(projectId);
    if (requestId !== projectDetailsGenRef.current) return;
    setProjectDetailsStatus(result ? 'loaded' : 'failed');
    setProjectNeo4jDatabase(result?.config?.graph.database_name || null);
    setSelectedProjectName(result?.projectName || null);
    if (result) {
      setCachedSelectedProject(result);
    }
  };

  const reloadProjectDetails = () => {
    if (!selectedProjectId) return;
    setProjectDetailsStatus('loading');
    void setProjectDetails(selectedProjectId, ++projectDetailsGenRef.current);
  };

  useEffect(() => {
    const requestId = ++projectDetailsGenRef.current;
    setProjectDetailsStatus(selectedProjectId ? 'loading' : 'idle');
    if (!selectedProjectId) {
      setCachedSelectedProject(null);
      setDropdownListCache([]);
      setDropdownHasMore(true);
      setProjectNeo4jDatabase(null);
      setSelectedProjectName(null);
      return;
    }
    setCachedSelectedProject((prev) =>
      prev?.projectId === selectedProjectId ? prev : null
    );
    void setProjectDetails(selectedProjectId, requestId);
  }, [selectedProjectId]);

  return (
    <ProjectContext.Provider
      value={{
        projects,
        dropdownListCache,
        dropdownHasMore,
        replaceDropdownListCache,
        cachedSelectedProject,
        setCachedSelectedProject,
        fetchProjects,
        projectsRefreshKey,
        notifyProjectsChanged,
        deletedProjectIds,
        markProjectDeleted,
        loadingProjects,
        selectedProjectId,
        setSelectedProjectId,
        setSelectedProjectName,
        projectNeo4jDatabase,
        projectDetailsStatus,
        reloadProjectDetails,
        selectedProjectName,
        isProjectSelectorVisible,
        setProjectSelectorVisible,
        selectedAdminPanelProjectId,
        selectedAdminPanelProjectDBName,
        setSelectedAdminPanelProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

/**
 * Read from `ProjectContext`. Without arguments, returns the full context (typed
 * as `ProjectContextType`). With a selector, returns the selector's result type
 * so callers preserve type safety on derived reads.
 */
export function useProjectContext(): ProjectContextType;
export function useProjectContext<T>(
  selector: (state: ProjectContextType) => T
): T;
export function useProjectContext<T>(
  selector?: (state: ProjectContextType) => T
): T | ProjectContextType {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return selector ? selector(context) : context;
}
